import React from 'react';
import { Drawer, Checkbox, CloseButton, Badge } from '@mantine/core';
import type { CategoryView } from '../../../../../../services/categories/categories.service';
import type { ProductView } from '../../../../../../services/products/products.service';
import { Button } from '../../../../../../components/Button';
import shared from '../../shared/settings-shared.module.scss';
import classes from './category-membership-drawer.module.scss';

const CATEGORY_COLORS = ['teal', 'blue', 'grape', 'orange', 'pink', 'cyan', 'lime', 'indigo', 'red', 'green'] as const;
// Summary: Stable per-category color so product category badges keep the same hue everywhere.
// Details: docs/function-library/features/category-report-group/category-report-group.md#category-manager-flow
const categoryColor = (categoryId: number): string => CATEGORY_COLORS[categoryId % CATEGORY_COLORS.length];

export type CategoryMembershipDrawerProps = {
  category: CategoryView | null;
  activeProducts: ProductView[];
  categoryById: Map<number, CategoryView>;
  checkedProductIds: Set<number>;
  isSaving: boolean;
  onToggleProduct: (productId: number) => void;
  onSave: () => void;
  onClose: () => void;
};

// Summary: Presentational right-side drawer for batch category membership.
// Details: docs/function-library/features/category-report-group/category-report-group.md#category-manager-flow
const CategoryMembershipDrawerComponent: React.FC<CategoryMembershipDrawerProps> = ({
  category,
  activeProducts,
  categoryById,
  checkedProductIds,
  isSaving,
  onToggleProduct,
  onSave,
  onClose,
}) => (
  <Drawer.Root opened={category !== null} onClose={isSaving ? () => undefined : onClose} position="right" size={520} padding={0}>
    <Drawer.Overlay />
    <Drawer.Content>
      <div className={classes.drawerContent}>
        <div className={classes.drawerHead}>
          <div>
            <div className={classes.drawerTitle}>שיוך מוצרים</div>
            <span className={classes.drawerSub}>{category?.name ?? ''}</span>
          </div>
          <span className={classes.drawerSpacer} />
          <CloseButton aria-label="סגור" data-testid="membership-close" onClick={onClose} size="lg" radius="xl" disabled={isSaving} />
        </div>

        <div className={classes.drawerBody}>
          <ul className={classes.productList}>
            {activeProducts.length === 0 && <li className={shared.empty}>אין מוצרים פעילים.</li>}
            {activeProducts.map((product) => {
              const currentCategory = product.categoryId != null ? categoryById.get(product.categoryId) : undefined;
              return (
                <li key={product.id} className={classes.productRow}>
                  <Checkbox
                    checked={checkedProductIds.has(product.id)}
                    disabled={isSaving || product.categoryId === category?.id}
                    onChange={() => onToggleProduct(product.id)}
                    data-testid={`membership-product-${product.id}`}
                    radius="sm"
                    classNames={{ root: classes.productToggle, body: classes.productCheckboxBody, labelWrapper: classes.productLabelWrapper }}
                    label={
                      <span className={classes.productLabel}>
                        <span className={classes.productName}>{product.name}</span>
                        {currentCategory && (
                          <Badge color={categoryColor(currentCategory.id)} variant="light" radius="sm" className={classes.productCategoryBadge}>
                            {currentCategory.name}
                          </Badge>
                        )}
                      </span>
                    }
                  />
                </li>
              );
            })}
          </ul>
        </div>

        <div className={classes.drawerFoot}>
          <span className={classes.drawerFootMsg}>{checkedProductIds.size} נבחרו</span>
          <Button variant="cancel" onClick={onClose} disabled={isSaving} dataTestId="membership-cancel">
            ביטול
          </Button>
          <Button variant="save" onClick={onSave} disabled={isSaving} loading={isSaving} loadingLabel="שומר..." dataTestId="save-membership">
            שמור קטגוריה
          </Button>
        </div>
      </div>
    </Drawer.Content>
  </Drawer.Root>
);

export const CategoryMembershipDrawer = React.memo(CategoryMembershipDrawerComponent);
